import type { ApiEndpoint, CmsModel } from "~/types/index.js";
import { renderListFilterFields } from "~/utils/renderListFilterFields.js";
import { renderSortEnum } from "~/utils/renderSortEnum.js";
import { renderFields } from "~/utils/renderFields.js";
import { renderGetFilterFields } from "~/utils/renderGetFilterFields.js";
import { ENTRY_META_FIELDS, isDateTimeEntryMetaField } from "~/constants.js";
import type {
    CmsModelFieldToGraphQLRegistry,
    CmsGraphQLSchemaSorter
} from "~/features/graphql/index.js";

interface CreateReadSDLParams {
    models: CmsModel[];
    model: CmsModel;
    fieldRegistry: CmsModelFieldToGraphQLRegistry.Interface;
    sorters: CmsGraphQLSchemaSorter.Interface[];
}

interface CreateReadSDL {
    (params: CreateReadSDLParams): string;
}

export const createReadSDL: CreateReadSDL = ({ models, model, fieldRegistry, sorters }): string => {
    const type: ApiEndpoint = "read";

    const fieldsRender = renderFields({
        models,
        model,
        fields: model.fields,
        type,
        fieldRegistry
    });

    const listFilterFieldsRender = renderListFilterFields({
        model,
        fields: model.fields,
        type,
        fieldRegistry
    });
    const sortEnumRender = renderSortEnum({
        model,
        fields: model.fields,
        fieldRegistry,
        sorters
    });
    const getFilterFieldsRender = renderGetFilterFields({
        fields: model.fields,
        fieldRegistry
    });

    const hasModelIdField = model.fields.some(f => f.fieldId === "modelId");

    const { singularApiName: singularName, pluralApiName: pluralName } = model;

    const onByMetaGqlFields = ENTRY_META_FIELDS.map(field => {
        const fieldType = isDateTimeEntryMetaField(field) ? "DateTime" : "CmsIdentity";

        return `${field}: ${fieldType}`;
    }).join("\n");

    return `
        """${model.description || singularName}"""
        type ${singularName}Values {
            ${fieldsRender.map(f => f.fields).join("\n") || "_empty: String"}
        }
        
        type ${singularName} {
            id: ID!
            entryId: String!
            ${hasModelIdField ? "" : "modelId: String!"}
            
            ${onByMetaGqlFields}
            values: ${singularName}Values
        }
        
        ${fieldsRender
            .map(f => f.typeDefs)
            .filter(Boolean)
            .join("\n")}
            
        input ${singularName}GetWhereInputValues {
            ${getFilterFieldsRender.fieldFiltersAsString() || "_empty: String"}
        }
        
        input ${singularName}GetWhereInput {
            ${getFilterFieldsRender.baseFiltersAsString()}
            values: ${singularName}GetWhereInputValues
        }
        
        input ${singularName}ListWhereInputValues {
            ${listFilterFieldsRender.fieldFiltersAsString() || "_empty: String"}
        }
        
        input ${singularName}ListWhereInput {
            ${listFilterFieldsRender.baseFiltersAsString()}
            
            values: ${singularName}ListWhereInputValues
            AND: [${singularName}ListWhereInput!]
            OR: [${singularName}ListWhereInput!]
        }
        
        
        enum ${singularName}ListSorter {
            ${sortEnumRender}
        }
        
        type ${singularName}Response {
            data: ${singularName}
            error: CmsError
        }
        
        type ${singularName}ListResponse {
            data: [${singularName}]
            meta: CmsListMeta
            error: CmsError
        }
        
        extend type Query {
            get${singularName}(where: ${singularName}GetWhereInput!): ${singularName}Response

            list${pluralName}(
                where: ${singularName}ListWhereInput
                sort: [${singularName}ListSorter]
                limit: Int
                after: String
                search: String
            ): ${singularName}ListResponse
        }
    `;
};
